import { 
  Autocomplete, Checkbox, InputLabel,
  TextField, Chip, Popper
} from '@mui/material';
import { makeId } from '@/common/helpers/helpers';
import './dropdown.scss';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

// Custom Popper component to ensure dropdown opens below
const CustomPopper = function (props) {
  return (
    <Popper 
      {...props} 
      placement="bottom-start"
      style={{ 
        zIndex: 1400, 
        position: 'absolute',
        width: props.anchorEl ? props.anchorEl.clientWidth : 'auto'
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        },
        {
          name: 'flip',
          enabled: false
        },
        {
          name: 'preventOverflow',
          enabled: false
        }
      ]}
    />
  );
};

function Dropdown({
  options, onChange, placeholder = '', label = '', size = 'small', loading = false,
  multiple = false, inputLabel = "", value="", name="",
  formik=null, isCheckBox = false, disabled = false, ...rest
}) {
  const {formatMessage} = useIntl();

  let valueUpdated = value || "";
  if(value && typeof value === "object" && !Array.isArray(value) &&
   Object.keys(value).length === 0 ){
    valueUpdated = "";
  } else if(multiple && !value){
    valueUpdated = [];
  }

  let updatedOptions = [...options];
  if (valueUpdated && typeof valueUpdated === "object" && !Array.isArray(valueUpdated)
     && valueUpdated.id && !updatedOptions.some(option => option.id == valueUpdated.id)) {
    updatedOptions = [valueUpdated, ...updatedOptions];
  } else if (multiple && Array.isArray(valueUpdated)) {
    valueUpdated.forEach(v => {
      if (v && typeof v === "object" && !updatedOptions.some(option => option.id == v.id)) {
        updatedOptions = [v, ...updatedOptions];
      }
    });
  }

  // Normalize value to option objects when possible so Autocomplete can match selections
  let valueForAutocomplete = valueUpdated;
  if (multiple && Array.isArray(valueUpdated)) {
    valueForAutocomplete = valueUpdated.map((v) => {
      if (v && typeof v === 'object') return v;
      const found = updatedOptions.find(opt => (opt?.id ?? opt) == v || (opt?.label || opt?.name) == v);
      return found || { id: v, label: String(v) };
    });
  } else if (!multiple) {
    if (valueUpdated && typeof valueUpdated !== 'object') {
      const found = updatedOptions.find(opt => (opt?.id ?? opt) == valueUpdated || (opt?.label || opt?.name) == valueUpdated);
      if (found) valueForAutocomplete = found;
    }
  }

  return (
    <> 
      {!!inputLabel && 
        <InputLabel className='labelClr' required={!formik?.fields[name]?.spec?.optional}>
          <FormattedMessage id={inputLabel} />
        </InputLabel>
      }
      <Autocomplete
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          if (!option) return '';
          return option.label || option.name || '';
        }}
        filterOptions={(allOptions, { inputValue }) => {
          const searchText = inputValue ? String(inputValue).toLowerCase().trim() : '';

          // NO SEARCH: Show all (Select All will auto-hide selected due to filterSelectedOptions)
          if (!searchText) {
            let result = allOptions.filter(opt => !opt?.all);
            
            // Add Select All at top for multiple
            if (multiple && result.length > 0) {
              result = [{ label: formatMessage({ id: 'Select All' }), all: true }, ...result];
            }
            
            return result;
          }

          // SEARCH ACTIVE: show items matching the substring or matching id
          return allOptions.filter(option => {
            if (!option || option.all) return false;

            const text = String(option.label || option.name || '');
            const textLower = text.toLowerCase().trim();
            const idStr = option?.id != null ? String(option.id).toLowerCase().trim() : '';

            return textLower.includes(searchText) || idStr.includes(searchText);
          });
        }}
        filterSelectedOptions={false}
        style={{ width: '100%' }}
        multiple={multiple}
        limitTags={1}
        size={size || 'small'}
        fullWidth
        disablePortal={true}
        PopperComponent={CustomPopper}
        id={`autocomplete_${makeId()}`}
        options={updatedOptions}
        loading={loading}
        noOptionsText="No matching options"
        renderInput={(params) => (
          <TextField {...params}
            placeholder={placeholder ? formatMessage({id: placeholder}) : ""}
            label={label}
            error={formik?.touched[name] && Boolean(formik?.errors[name])}
            helperText={formik?.touched[name] && formik?.errors[name]}
          />
        )}
        onChange={(event, newValueRaw) => {
          let newValue = JSON.parse(JSON.stringify(newValueRaw));
          if(isCheckBox && multiple && newValue.find(option => option.all)) {
            newValue = (value?.length === options.length ? [] : options)
          }
          if (formik) {
            formik.setFieldValue(name, newValue);
            onChange(newValue, event)
          } else {
             onChange(newValue, event)
          }
        }}
        value={valueForAutocomplete}
        disabled={disabled}
        isOptionEqualToValue={(option, val) => {
          const optId = option?.id ?? option;
          const valId = val?.id ?? val;

          // Direct id/value comparison (allow type coercion)
          if (optId == valId) return true;

          // Compare label/name against primitive value or value label
          const optLabel = option?.label ?? option?.name ?? '';
          const valLabel = val?.label ?? val?.name ?? val;
          if (String(optLabel) === String(valLabel)) return true;

          // As a last resort, compare deep equality (safe JSON stringify)
          try {
            if (JSON.stringify(option) === JSON.stringify(val)) return true;
          } catch (e) {
            // ignore
          }

          return false;
        }
        }
        {...(multiple ? {
          renderTags: (tagValue, getTagProps) => {
            return [
              <Chip
                key="count"
                label={`${tagValue.length} selected`}
                size="small"
                color="primary"
                variant="outlined"
              />
            ];
          }
        } : {})}
        {...(isCheckBox ? {
          renderOption: (props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                style={{ marginRight: 8 }}
                checked={option.all ? !!(value?.length === options.length) : selected}
              />
              {option.label}
            </li>
          ),
          disableCloseOnSelect: true,
        } : {})}
        {...(rest || {})}
      />
    </>
  );
}

Dropdown.propTypes = {
  options: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  label: PropTypes.string,
  size: PropTypes.string,
  loading: PropTypes.bool,
  multiple: PropTypes.bool,
  inputLabel: PropTypes.string,
  value: PropTypes.any,
  name: PropTypes.string,
  formik: PropTypes.object,
  isCheckBox: PropTypes.bool,
  disabled: PropTypes.bool
}

Dropdown.defaultProps = {
  placeholder: "",
  label: "",
  size: "",
  loading: "",
  multiple: false,
  inputLabel: "",
  value: null,
  formik: null,
  name: "",
  onChange: () => {},
  isCheckBox: false,
  disabled: false
}

export default Dropdown;
